
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Box,
  HStack,
  IconButton,
  Text,
  Badge,
} from '@chakra-ui/react'
import { GripVertical, Trash2 } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { PageBlock } from '@/types/pageBuilder'
import { BlockPreview } from './BlockPreview'

interface SortableBlockProps {
  block: PageBlock
  onSelect: () => void
  onDelete: () => void
  isSelected?: boolean
}

export function SortableBlock({ block, onSelect, onDelete, isSelected }: SortableBlockProps) {
  const colors = useThemeColors()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getBlockTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      heading: 'Heading',
      text: 'Paragraph',
      image: 'Image',
      video: 'Video',
      button: 'Button',
      spacer: 'Spacer',
      divider: 'Divider',
      columns: 'Columns',
      quote: 'Quote',
      list: 'List',
      code: 'Code',
      html: 'HTML',
    }
    return labels[type] || type
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      border="2px"
      borderColor={isSelected ? 'blue.500' : 'transparent'}
      borderRadius="md"
      bg={colors.cardBg}
      position="relative"
      cursor="pointer"
      _hover={{
        borderColor: isSelected ? 'blue.500' : colors.border,
        boxShadow: 'sm',
      }}
      transition="all 0.2s"
      onClick={onSelect}
    >
      {/* Block Toolbar - Shows on hover/select */}
      <Box
        position="absolute"
        top={-10}
        left={0}
        right={0}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        zIndex={20}
        opacity={isSelected ? 1 : 0}
        transition="opacity 0.2s"
        pointerEvents={isSelected ? 'auto' : 'none'}
      >
        <HStack spacing={1} bg="blue.500" px={2} py={1} borderRadius="md">
          <Badge colorScheme="blue" fontSize="xs" bg="blue.600" color="white">
            {getBlockTypeLabel(block.type)}
          </Badge>
        </HStack>
        <HStack spacing={1} bg="blue.500" px={2} py={1} borderRadius="md">
          <IconButton
            aria-label="Delete block"
            icon={<Trash2 size={14} />}
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            colorScheme="blue"
            color="white"
            _hover={{ bg: 'blue.600' }}
          />
        </HStack>
      </Box>

      {/* Drag Handle - Left side */}
      <Box
        position="absolute"
        left={-15}
        top="50%"
        transform="translateY(-50%)"
        {...attributes}
        {...listeners}
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        p={1}
        bg={colors.cardBg}
        border="1px"
        borderColor={colors.border}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
        opacity={isSelected ? 1 : 0}
        transition="opacity 0.2s"
        _hover={{ opacity: 1 }}
        zIndex={10}
      >
        <GripVertical size={16} color={colors.textMuted} />
      </Box>

      {/* Block Content */}
      <Box p={4} minH="60px">
        <BlockPreview block={block} />
      </Box>
    </Box>
  )
}

